import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import GigCard from "../../components/Gigs/GigCard";
import GigPlaceholder from "../../components/common/Placeholders/GigPlaceholder";
import { filterGigs } from "../../store/thunks/gigsThunk";
import { colors } from "../../utils/Colors";
import {
  buildBreadcrumbLD,
  buildCanonical,
  buildItemListLD,
  buildTitle,
  jsonLdString,
} from "../../utils/seo";

/**
 * SEO landing page for a service category.
 * Route: /services/:category
 * Route: /services/:category/:subcategory
 */
const CategoryPage = () => {
  const { category: categoryParam, subcategory: subcategoryParam } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { categories } = useSelector((state) => state.categories);
  const { gigs, loading } = useSelector((state) => state.gigs);

  // Resolve category and subcategory objects from names in the URL
  const categoryObj = categories.find(
    (c) => c.name === decodeURIComponent(categoryParam)
  );
  const subcategoryObj = subcategoryParam
    ? categoryObj?.subcategories?.find(
        (s) => s.name === decodeURIComponent(subcategoryParam)
      )
    : null;

  useEffect(() => {
    if (!categoryObj) return;
    dispatch(
      filterGigs({
        category: categoryObj._id,
        subcategory: subcategoryObj?._id || "",
      })
    );
  }, [dispatch, categoryObj?._id, subcategoryObj?._id]); // eslint-disable-line

  const catName = decodeURIComponent(categoryParam || "");
  const subName = subcategoryParam ? decodeURIComponent(subcategoryParam) : null;
  const pageTitle = subName
    ? `${subName} Services in ${catName}`
    : `${catName} Services`;
  const canonical = buildCanonical(
    subName
      ? `/services/${encodeURIComponent(catName)}/${encodeURIComponent(subName)}`
      : `/services/${encodeURIComponent(catName)}`
  );
  const description = subName
    ? `Hire expert ${subName} freelancers in the ${catName} category on GigFalcon. Browse top-rated gigs and get your project done fast.`
    : `Browse all ${catName} services on GigFalcon. Find skilled professionals across every ${catName} subcategory and hire today.`;

  // Build breadcrumbs
  const breadcrumbs = [
    { name: "Home", url: "https://www.gigfalcon.com/" },
    { name: catName, url: `https://www.gigfalcon.com/services/${encodeURIComponent(catName)}` },
  ];
  if (subName) {
    breadcrumbs.push({
      name: subName,
      url: `https://www.gigfalcon.com/services/${encodeURIComponent(catName)}/${encodeURIComponent(subName)}`,
    });
  }

  // Build subcategory list for ItemList JSON-LD on category pages
  const subcategoryLinks = !subName && categoryObj?.subcategories
    ? categoryObj.subcategories.map((s) => ({
        name: s.name,
        url: `https://www.gigfalcon.com/services/${encodeURIComponent(catName)}/${encodeURIComponent(s.name)}`,
      }))
    : [];

  return (
    <PageWrapper colors={colors}>
      <Helmet>
        <title>{buildTitle(pageTitle)}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${pageTitle} | GigFalcon`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{jsonLdString(buildBreadcrumbLD(breadcrumbs))}</script>
        {subcategoryLinks.length > 0 && (
          <script type="application/ld+json">
            {jsonLdString(buildItemListLD(pageTitle, canonical, subcategoryLinks))}
          </script>
        )}
      </Helmet>

      <Header />

      <div className="container mt-4">
        {/* Breadcrumb nav */}
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className={`breadcrumb-item${!subName ? " active" : ""}`}>
              {subName ? (
                <Link to={`/services/${encodeURIComponent(catName)}`}>{catName}</Link>
              ) : (
                catName
              )}
            </li>
            {subName && (
              <li className="breadcrumb-item active" aria-current="page">
                {subName}
              </li>
            )}
          </ol>
        </nav>

        <h1 className="mb-1">{pageTitle}</h1>
        <p className="text-muted mb-4">{description}</p>

        {/* Subcategory chips (only on category root) */}
        {!subName && categoryObj?.subcategories?.length > 0 && (
          <div className="mb-4 d-flex flex-wrap gap-2">
            {categoryObj.subcategories.map((sub) => (
              <Link
                key={sub._id}
                to={`/services/${encodeURIComponent(catName)}/${encodeURIComponent(sub.name)}`}
                className="btn btn-outline-secondary btn-sm"
              >
                {sub.name}
              </Link>
            ))}
          </div>
        )}

        {/* Gig grid */}
        <div className="d-flex flex-wrap gap-3 mb-5">
          {loading ? (
            <GigPlaceholder placeholders={[1, 2, 3, 4, 5, 6]} className="gig-list" />
          ) : !loading && (!gigs || gigs.length === 0) ? (
            <p className="text-center mt-4 mb-5 text-muted w-100">
              No gigs found for this category yet. Check back soon!
            </p>
          ) : (
            gigs?.map((gig, i) => <GigCard key={i} {...gig} />)
          )}
        </div>
      </div>

      <Footer />
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  background: ${({ colors }) => colors?.background || "#fff"};
  min-height: 100vh;
`;

export default CategoryPage;
